import axios from "axios";
import { PREVIEW_SITE_URL, PRODUCTION_SITE_URL, buildPublicUrl } from "../../Helper/siteLinks";
import { parseApiDate } from "../../Helper/dates";

/**
 * Types + petits appels axios partagés par la page Redirections, la pastille de navigation,
 * la modale d'impact de dépublication et la modale « Corriger à la source ». Tout passe par
 * l'axios global (l'intercepteur de l'app gère l'authentification) : jamais de fetch() brut.
 */

// ---------------------------------------------------------------------------
// Règles de redirection
// ---------------------------------------------------------------------------

/** Une règle de redirection servie par le site public. */
export interface Redirect {
	id: string;
	fromPath: string;
	toPath: string;
	isPermanent: boolean;
	source: string;
	hits: number;
}

/** Réponse de GET /api/redirects. */
export interface RedirectListResponse {
	items: Redirect[];
	total: number;
}

// ---------------------------------------------------------------------------
// Tâches de cohérence des liens
// ---------------------------------------------------------------------------

export type RedirectTaskKind = "broken-inbound-link" | "broken-redirect-target" | "missing-page";
export type RedirectTaskEnvironment = "preview" | "production";
export type RedirectTaskSeverity = "warning" | "error";
export type RedirectTaskStatus = "open" | "resolved" | "dismissed";

/** Une tâche de cohérence : quelque chose s'est cassé au retrait d'un contenu. */
export interface RedirectTask {
	id: string;
	kind: RedirectTaskKind | string;
	url: string;
	environment: RedirectTaskEnvironment | string;
	severity: RedirectTaskSeverity | string;
	status: RedirectTaskStatus | string;
	sourceContentId?: string | null;
	sourceTitle?: string | null;
	sourceSchemaKey?: string | null;
	/** Chaîne JSON : {"staticLinks":[…],"targetingRedirects":[…]} : à parser défensivement. */
	detailsJson?: string | null;
	resolvedRedirectId?: string | null;
	resolvedBy?: string | null;
	createdAt?: string | null;
	resolvedAt?: string | null;
}

/** Réponse de GET /api/redirects/tasks. */
export interface RedirectTaskListResponse {
	items: RedirectTask[];
	total: number;
}

/** Réponse de GET /api/redirects/tasks/count : production = tâches d'erreur ouvertes. */
export interface RedirectTaskCount {
	production: number;
	preview: number;
}

/** Un emplacement cassé : quel document pointe encore vers l'URL retirée, et où. */
export interface ImpactLocation {
	contentId?: string | null;
	schemaKey?: string | null;
	title?: string | null;
	slug?: string | null;
	linkLabel?: string | null;
	linkPath?: string | null;
}

/** Règle de redirection minimale telle qu'embarquée dans detailsJson. */
export interface ImpactRedirectRef {
	id?: string | null;
	fromPath?: string | null;
	toPath?: string | null;
	isPermanent?: boolean;
}

/** Contenu parsé de RedirectTask.detailsJson. */
export interface RedirectTaskDetails {
	staticLinks: ImpactLocation[];
	targetingRedirects: ImpactRedirectRef[];
}

/** Réponse de GET /api/redirects/impact : ce que retirer ce contenu casserait. */
export interface RemovalImpact {
	contentId?: string | null;
	isDeleted?: boolean | null;
	url: string;
	previewUrls: string[];
	productionUrls: string[];
	staticLinks: ImpactLocation[];
	targetingRedirects: Redirect[];
	hasImpact: boolean;
}

/** Réponse des POST fix-source (variante tâche ou pré-retrait). */
export interface FixSourceResult {
	rewrittenContentIds: string[];
	repointedRedirects: string[];
	refusedRedirects: Array<{ fromPath?: string | null; message?: string | null }>;
	taskResolved: boolean;
	remainingStaticLinks: number;
	remainingTargetingRedirects: number;
}

/** Réponse de POST /api/redirects/{id}/fix-links. */
export interface FixLinksResult {
	rewrittenContentIds: string[];
	resolvedPageId?: string | null;
}

// ---------------------------------------------------------------------------
// Helpers d'affichage
// ---------------------------------------------------------------------------

/** Extrait un message lisible d'une erreur axios/inconnue (les 400 métier portent {message}). */
export const extractErrorMessage = (error: unknown): string | null => {
	if (error && typeof error === "object" && "response" in error && (error as any).response) {
		const response = (error as any).response;
		const message = response?.data?.message ?? response?.data ?? response?.statusText ?? null;
		if (typeof message === "string") {
			const truncated = message.split(" at BCC.ADMIN")[0]?.trim();
			return truncated?.length ? truncated : message;
		}
		return null;
	}
	if (error instanceof Error) {
		return error.message;
	}
	return null;
};

/** Parse défensivement detailsJson (chaîne JSON, possiblement null/corrompue). */
export const parseTaskDetails = (detailsJson?: string | null): RedirectTaskDetails => {
	const empty: RedirectTaskDetails = { staticLinks: [], targetingRedirects: [] };
	if (!detailsJson || typeof detailsJson !== "string") return empty;
	try {
		const parsed = JSON.parse(detailsJson);
		return {
			staticLinks: Array.isArray(parsed?.staticLinks) ? parsed.staticLinks : [],
			targetingRedirects: Array.isArray(parsed?.targetingRedirects) ? parsed.targetingRedirects : []
		};
	} catch {
		return empty;
	}
};

/** Libellés français des genres de tâche. */
export const TASK_KIND_LABELS: Record<string, string> = {
	"broken-inbound-link": "Liens entrants cassés",
	"broken-redirect-target": "Redirection orpheline",
	"missing-page": "Page manquante"
};

export const taskKindLabel = (kind?: string | null): string =>
	(kind && TASK_KIND_LABELS[kind]) || "Lien cassé";

/** Les URL de tâche sont normalisées (sans hôte ni slash initial) : préfixe pour l'affichage. */
export const displayPath = (url?: string | null): string => {
	const value = (url ?? "").trim();
	if (!value) return "/";
	if (/^https?:\/\//i.test(value)) return value;
	return value.startsWith("/") ? value : `/${value}`;
};

/** URL publique cliquable pour l'environnement de la tâche (préview ou production). */
export const publicUrlFor = (environment: string | null | undefined, url: string | null | undefined): string => {
	const base = environment === "production" ? PRODUCTION_SITE_URL : PREVIEW_SITE_URL;
	return buildPublicUrl(base, url ?? "");
};

/** Date fr-FR courte (« 16 juil. 2026, 14:05 ») : tolère null/invalide. */
export const formatTaskDate = (value?: string | null): string => {
	const date = parseApiDate(value ?? undefined);
	if (!date) return "-";
	return date.toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
};

/** Route de l'éditeur admin pour un contenu (même logique que le navigateur de contenus). */
export const editorRouteFor = (schemaKey?: string | null, contentId?: string | null): string | null => {
	if (!contentId) return null;
	const key = (schemaKey ?? "").trim().toLowerCase();
	if (key === "page") return `/page/${encodeURIComponent(contentId)}`;
	if (key === "layout") return "/layout";
	if (key === "languages") return "/languages";
	if (key === "contact-info") return "/contact-info";
	if (!key) return null;
	return `/content/${encodeURIComponent(key)}/${encodeURIComponent(contentId)}`;
};

// ---------------------------------------------------------------------------
// Appels API
// ---------------------------------------------------------------------------

export const fetchRedirects = async (search: string | undefined, page: number, limit: number): Promise<RedirectListResponse> => {
	const response = await axios.get<RedirectListResponse>("/api/redirects", {
		params: { search: search || undefined, page, limit }
	});
	return { items: response.data?.items ?? [], total: response.data?.total ?? 0 };
};

export const createRedirect = async (fromPath: string, toPath: string, isPermanent: boolean): Promise<string | null> => {
	const response = await axios.post<{ id?: string }>("/api/redirects", { fromPath, toPath, isPermanent });
	return response.data?.id ?? null;
};

export const deleteRedirect = async (id: string): Promise<void> => {
	await axios.delete(`/api/redirects/${encodeURIComponent(id)}`);
};

/** Vide TOUTES les tâches de redirection (tous statuts) et invalide leurs notifications.
 * Suppression réelle, pas un dismiss de masse. Retourne le nombre supprimé. */
export const clearAllTasks = async (): Promise<number> => {
	const response = await axios.delete<{ deleted?: number }>("/api/redirects/tasks/all", {
		params: { confirm: "SUPPRIMER" }
	});
	return Number(response.data?.deleted) || 0;
};

/** Une ligne d'import refusée, avec la raison exacte du garde anti-boucle. */
export interface RedirectImportRefusal {
	from: string;
	to: string;
	raison: string;
}

/** Résultat d'un import en masse : plus aucune ligne n'est sautée en silence. */
export interface RedirectImportResult {
	imported: number;
	refused: RedirectImportRefusal[];
}

export const importRedirects = async (
	rows: Array<{ from: string; to: string; permanent?: boolean }>
): Promise<RedirectImportResult> => {
	const response = await axios.post<RedirectImportResult>("/api/redirects/import", rows);
	return {
		imported: Number(response.data?.imported) || 0,
		refused: Array.isArray(response.data?.refused) ? response.data.refused : []
	};
};

export const publishRedirects = async (production: boolean): Promise<void> => {
	await axios.post("/api/redirects/publish", null, { params: { production } });
};

export const fetchTasks = async (
	environment: RedirectTaskEnvironment,
	status: RedirectTaskStatus | "all",
	page: number,
	limit: number
): Promise<RedirectTaskListResponse> => {
	const response = await axios.get<RedirectTaskListResponse>("/api/redirects/tasks", {
		params: { environment, status, page, limit }
	});
	return { items: response.data?.items ?? [], total: response.data?.total ?? 0 };
};

export const fetchTaskCount = async (): Promise<RedirectTaskCount> => {
	const response = await axios.get<RedirectTaskCount>("/api/redirects/tasks/count");
	return {
		production: Number(response.data?.production) || 0,
		preview: Number(response.data?.preview) || 0
	};
};

export const dismissTask = async (id: string): Promise<void> => {
	await axios.post(`/api/redirects/tasks/${encodeURIComponent(id)}/dismiss`);
};

export const reopenTask = async (id: string): Promise<void> => {
	await axios.post(`/api/redirects/tasks/${encodeURIComponent(id)}/reopen`);
};

export const fixTaskSource = async (taskId: string, newContentId: string): Promise<FixSourceResult> => {
	const response = await axios.post<FixSourceResult>(
		`/api/redirects/tasks/${encodeURIComponent(taskId)}/fix-source`,
		{ newContentId }
	);
	return response.data;
};

export const fixContentSource = async (oldContentId: string, newContentId: string): Promise<FixSourceResult> => {
	const response = await axios.post<FixSourceResult>("/api/redirects/fix-source", { oldContentId, newContentId });
	return response.data;
};

export const fixRuleLinks = async (ruleId: string): Promise<FixLinksResult> => {
	const response = await axios.post<FixLinksResult>(`/api/redirects/${encodeURIComponent(ruleId)}/fix-links`);
	return response.data;
};

export const fetchImpact = async (contentId: string): Promise<RemovalImpact> => {
	const response = await axios.get<RemovalImpact>("/api/redirects/impact", { params: { contentId } });
	const data = response.data ?? ({} as RemovalImpact);
	return {
		contentId: data.contentId ?? contentId,
		isDeleted: data.isDeleted ?? null,
		url: data.url ?? "",
		previewUrls: Array.isArray(data.previewUrls) ? data.previewUrls : [],
		productionUrls: Array.isArray(data.productionUrls) ? data.productionUrls : [],
		staticLinks: Array.isArray(data.staticLinks) ? data.staticLinks : [],
		targetingRedirects: Array.isArray(data.targetingRedirects) ? data.targetingRedirects : [],
		hasImpact: Boolean(data.hasImpact)
	};
};
